import MapBaseClass from "./MapBaseClass.js";
import MapFloor from "./MapFloor.js";
import MapItem from "./MapItem.js";
import MapLink from "./MapLink.js";
import MapIntegrations from "@/helpers/maps/MapIntegrations.js";

export default class MapPlan extends MapBaseClass {
    constructor(data) {
        super(data);

        // for autocompletion
        this.floors = [];
        this.items = [];
        this.links = [];
        for (let i = 0; i < data.floors.length; i++) {
            this.floors.push(new MapFloor(data.floors[i]));
        }
        for (let i = 0; i < data.items.length; i++) {
            let mapItem = new MapItem(data.items[i], this);
            mapItem.index = `n${i}`;
            this.items.push(mapItem);
        }
        // Always initialize links after items
        for (let i = 0; i < data.links.length; i++) {
            let mapLink = new MapLink(data.links[i], this);
            mapLink.index = `l${i}`;
            this.links.push(mapLink);
        }

        if (this.floors.length > 1) {
            this.sortFloors();
        }

        this.integrations = new MapIntegrations(this.integrations);
    }

    getFloorById(id) {
        return this.floors.find(f => f.id === id);
    }

    getItemById(id) {
        return this.items.find(i => i.id === id);
    }

    getItemsByCmsItemId(cmsItemId) {
        return this.items.filter(i => i.integrations?.cms?.itemId === cmsItemId);
    }

    getItemsByType(type) {
        return this.items.filter(i => i.type === type);
    }

    getItemByQRCode(qrCode) {
        return this.items.find(i => i.qrCode === qrCode);
    }

    // Read query parameters from a resolved QR URL.
    getQrSearchParams(value) {
        try {
            const url = new URL(value, window.location.origin);
            return url.searchParams;
        } catch {
            try {
                return new URLSearchParams(value);
            } catch {
                return null;
            }
        }
    }

    // Resolve a scanned QR URL into exact map items.
    getQrScanTarget(value) {
        const searchParams = this.getQrSearchParams(value);
        if (!searchParams) {
            return null;
        }

        const qrCode = searchParams.get(`qr`);
        if (qrCode) {
            const startItem = this.getItemByQRCode(qrCode);
            if (startItem) {
                return { startItem };
            }

            return null;
        }

        const startValue = searchParams.get(`start`) || searchParams.get(`depart`);
        const endValue = searchParams.get(`end`) || searchParams.get(`arrivee`);
        if (startValue && endValue) {
            const startItem = this.getItemByPermalink(startValue);
            const endItem = this.getItemByPermalink(endValue);
            if (startItem && endItem) {
                return { startItem, endItem };
            }
        }

        return null;
    }

    getItemByPermalink(permalink) {
        return this.items.find(i => i.matchPermalink(permalink));
    }

    getItemsByPermalink(permalink) {
        return this.items.filter(i => i.matchPermalink(permalink));
    }

    getMainFloor() {
        if (this.floors.length === 0) {
            return null;
        } else if (this.floors.length === 1) {
            return this.floors[0];
        } else {
            const floor = this.floors.find(floor => {
                return floor.isMainFloor === true;
            });
            if (floor) {
                return floor;
            } else {
                return this.floors[0];
            }
        }
    }

    get360ImagesOffsetAngle() {
        let offset = 0;
        if (this.world?.enabled) { // apply north heading
            offset += parseFloat(this.world.northAngle);
        }
        // apply global image angle offset
        if (this.integrations.images360?.enabled) {
            offset += parseFloat(this.integrations.images360.offsetAngle);
        }

        return offset;
    }

    addFloor(floor) {
        this.floors.push(floor);
        this.sortFloors();
    }

    addLink(link) {
        this.links.push(link);
    }

    addItem(link) {
        this.items.push(link);
    }

    sortFloors() {
        this.floors.sort((a, b) => {
            return b.floor - a.floor;
        });
    }

    toJSON() {
        let data = {
            floors: [],
            items: [],
            links: []
        };
        for (let key in this) {
            switch (key) {
                case `floors`:
                    for (let floor of this.floors) {
                        if (!floor.deleted) {
                            data.floors.push(floor.toJSON());
                        }
                    }
                    break;
                case `items`:
                    for (let item of this.items) {
                        data.items.push(item.toJSON());
                    }
                    break;
                case `links`:
                    for (let link of this.links) {
                        data.links.push(link.toJSON());
                    }
                    break;
                default:
                    if (typeof this[key] !== `function`) {
                        data[key] = this[key];
                    }
                    break;
            }
        }
        return data;
    }
}
